import type { WatchConfigTableName } from './watchSocket.constants';
import type { WatchConfigOptions, WatchInboundMessage } from './watchSocket.types';
import { getConfigTableIdColumn, getConfigTableType } from './watchSocketConfigTables';

const COMMAND_KEY_PREFIX = 'command';
const CONFIG_KEY_PREFIX = 'config';
const ANY_RECORD = '*';

const toUuid = (hex: string): string =>
  `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;

// randomUUID is unavailable outside secure contexts, and the backend correlates on uuid shape.
export const createRequestId = (): string => {
  const { crypto } = globalThis;

  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);

  if (typeof crypto?.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256);
    });
  }

  // eslint-disable-next-line no-bitwise
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  // eslint-disable-next-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  return toUuid(Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(''));
};

export const buildCommandCorrelationKey = (requestId: string): string => `${COMMAND_KEY_PREFIX}:${requestId}`;

export const buildConfigCorrelationKey = (table: WatchConfigTableName, id?: string | number | null): string =>
  `${CONFIG_KEY_PREFIX}:${table}:${getConfigTableType(table)}:${id ?? ANY_RECORD}`;

const getAnnouncedRecordId = (table: WatchConfigTableName, options: WatchConfigOptions): string | null => {
  const id = options.id ?? options[getConfigTableIdColumn(table)];

  return id === undefined || id === null ? null : String(id);
};

/**
 * A reply correlates to a request either through the ``command_uuid`` the command was sent
 * with, or - for config - through the table it belongs to plus, when the reply echoes a single
 * record, that record's identifier. A full snapshot answers every pending request for its table.
 */
export const matchesCorrelationKey = (key: string, message: WatchInboundMessage): boolean => {
  if (key.startsWith(`${COMMAND_KEY_PREFIX}:`)) {
    return message.room === 'feedback' && key === buildCommandCorrelationKey(message.payload.command_uuid);
  }

  if (message.room !== 'config/announcements') {
    return false;
  }

  const { table, options } = message.payload;

  if (Array.isArray(options)) {
    return key.startsWith(`${CONFIG_KEY_PREFIX}:${table}:`);
  }

  if (key === buildConfigCorrelationKey(table)) {
    return true;
  }

  const recordId = getAnnouncedRecordId(table, options);

  return recordId !== null && key === buildConfigCorrelationKey(table, recordId);
};
